// SetupAccountsFaEx.js - Updated with new class names
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faArrowRight,
  faSyncAlt,
  faSave,
  faCalculator,
  faSearch,
  faChartLine,
  faFileExport,
  faInfoCircle,
  faExclamationTriangle,
  faPlus,
  faTrash,
  faEdit,
  faDownload,
  faFilter,
  faSortAmountDown,
  faSortAmountUp,
  faCalendarAlt,
  faMoneyBillWave,
  faBook,
  faGraduationCap,
  faDatabase,
  faHistory,
  faKey
} from "@fortawesome/free-solid-svg-icons";
import "./dashboard.css";
import "./setupAccountsFaEx.css";

const API_BASE_URL = "https://mhsdev.online/MHS-WEB/api";

const classes = [
  { name: "Play Group", value: "Play Group" },
  { name: "Nursery", value: "Nursery" },
  { name: "Prep", value: "Prep" },
  { name: "Class 1", value: "Class 1" },
  { name: "Class 2", value: "Class 2" },
  { name: "Class 3", value: "Class 3" },
  { name: "Class 4", value: "Class 4" },
  { name: "Class 5", value: "Class 5" },
  { name: "Class 6", value: "Class 6" },
  { name: "Class 7", value: "Class 7" },
  { name: "Class 8", value: "Class 8" },
  { name: "Class Pre 9", value: "Class Pre 9" },
  { name: "Class 9", value: "Class 9" },
  { name: "Class 10", value: "Class 10" }
];

const SetupAccountsFaEx = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const inputRefs = useRef([]);

  const initialClassName = location.state?.className || "Play Group";
  const currentClassObj = classes.find(c => c.name === initialClassName) || classes[0];
  const classIndex = classes.findIndex(c => c.name === initialClassName);

  const [formData, setFormData] = useState({
    id: null,
    class_name: currentClassObj.value,
    annual_charges: "",
    admission_fee: "",
    monthly_fee: "",
    book_charges: "",
    workbook_charges: "",
    total_payable: "0",
    created_at: "",
    updated_at: ""
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState({
    total_classes: 0,
    total_payable: 0,
    average_per_class: 0,
    last_updated: ""
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [isNewRecord, setIsNewRecord] = useState(false);
  const [activeTab, setActiveTab] = useState("form");
  const [auditLog, setAuditLog] = useState([]);

  // Initialize input focus and keyboard shortcuts
  useEffect(() => {
    if (inputRefs.current.length > 0 && !loading) {
      inputRefs.current[0]?.focus();
    }

    const handleShortcut = (e) => {
      if (e.altKey && e.key.toLowerCase() === "n") {
        e.preventDefault();
        handleNextClass();
      } else if (e.altKey && e.key.toLowerCase() === "p") {
        e.preventDefault();
        handlePreviousClass();
      } else if (e.ctrlKey && e.key === "s") {
        e.preventDefault();
        handleSave();
      } else if (e.key === "Escape") {
        e.preventDefault();
        clearForm();
      }
    };
    document.addEventListener("keydown", handleShortcut);
    return () => document.removeEventListener("keydown", handleShortcut);
  }, [classIndex, loading]);

  // Fetch class data on component mount or class change
  useEffect(() => {
    fetchClassData(currentClassObj.value);
    fetchStats();
    fetchAuditLog();
  }, [currentClassObj.value]);

  // Calculate total payable whenever any field changes
  useEffect(() => {
    calculateTotal();
  }, [
    formData.annual_charges,
    formData.admission_fee,
    formData.monthly_fee,
    formData.book_charges,
    formData.workbook_charges
  ]);

  const calculateTotal = () => {
    const total =
      (parseFloat(formData.annual_charges) || 0) +
      (parseFloat(formData.admission_fee) || 0) +
      (parseFloat(formData.monthly_fee) || 0) +
      (parseFloat(formData.book_charges) || 0) +
      (parseFloat(formData.workbook_charges) || 0);

    setFormData(prev => ({
      ...prev,
      total_payable: total.toFixed(2)
    }));
  };

  const validateForm = () => {
    const errors = {};
    const fields = ['annual_charges', 'admission_fee', 'monthly_fee', 'book_charges', 'workbook_charges'];

    fields.forEach(field => {
      const value = formData[field];
      if (value && (isNaN(value) || parseFloat(value) < 0)) {
        errors[field] = "Please enter a valid positive number";
      }
    });

    if (!formData.class_name || formData.class_name.trim() === '') {
      errors.class_name = "Class name is required";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const fetchClassData = async (className) => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/FeeExpensesController.php?action=getByClass&class_name=${encodeURIComponent(className)}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.data) {
        setIsNewRecord(!result.data.id);
        
        setFormData({
          id: result.data.id || null,
          class_name: result.data.class_name || className,
          annual_charges: result.data.annual_charges || "",
          admission_fee: result.data.admission_fee || "",
          monthly_fee: result.data.monthly_fee || "",
          book_charges: result.data.book_charges || "",
          workbook_charges: result.data.workbook_charges || "",
          total_payable: result.data.total_payable || "0",
          created_at: result.data.created_at || "",
          updated_at: result.data.updated_at || ""
        });
      } else {
        setIsNewRecord(true);
        setFormData(prev => ({
          ...prev,
          id: null,
          class_name: className,
          annual_charges: "",
          admission_fee: "",
          monthly_fee: "",
          book_charges: "",
          workbook_charges: "",
          total_payable: "0",
          created_at: "",
          updated_at: ""
        }));
      }
    } catch (error) {
      console.error("Error fetching class data:", error);
      Swal.fire({
        title: "Error!",
        text: "Failed to load class data. Please check your connection.",
        icon: "error",
        confirmButtonColor: "#d33",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/FeeExpensesController.php?action=getStats`
      );

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setStats(result.stats);
        }
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const fetchAuditLog = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/FeeExpensesController.php?action=getAuditLog&class_name=${encodeURIComponent(formData.class_name)}`
      );

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setAuditLog(result.data || []);
        }
      }
    } catch (error) {
      console.error("Error fetching audit log:", error);
    }
  };

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    
    if (!validateForm()) {
      Swal.fire({
        title: "Validation Error",
        text: "Please fix the errors in the form before saving.",
        icon: "warning",
        confirmButtonColor: "#f59e0b",
      });
      return;
    }

    setSaving(true);
    
    try {
      const action = isNewRecord ? 'create' : 'update';
      
      const response = await fetch(`${API_BASE_URL}/FeeExpensesController.php?action=${action}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (result.success) {
        await Promise.all([
          fetchClassData(formData.class_name),
          fetchStats(),
          fetchAuditLog()
        ]);

        Swal.fire({
          title: "Success!",
          text: result.message || "Operation completed successfully",
          icon: "success",
          confirmButtonColor: "#10b981",
          timer: 2000,
          timerProgressBar: true,
        });
        
        setIsNewRecord(false);
      } else {
        throw new Error(result.message || "Failed to save");
      }
    } catch (error) {
      console.error("Error saving data:", error);
      Swal.fire({
        title: "Error!",
        text: error.message || "Failed to save data",
        icon: "error",
        confirmButtonColor: "#d33",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (isNewRecord || !formData.id) {
      Swal.fire({
        title: "Cannot Delete",
        text: "This record doesn't exist in the database yet.",
        icon: "warning",
        confirmButtonColor: "#f59e0b",
      });
      return;
    }

    Swal.fire({
      title: "Are you sure?",
      text: `Delete fee structure for ${formData.class_name}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!"
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await fetch(
            `${API_BASE_URL}/FeeExpensesController.php?action=delete&class_name=${encodeURIComponent(formData.class_name)}`,
            {
              method: "DELETE",
            }
          );

          const result = await response.json();

          if (result.success) {
            Swal.fire({
              title: "Deleted!",
              text: "Record has been deleted.",
              icon: "success",
              confirmButtonColor: "#10b981",
            });
            
            if (classIndex < classes.length - 1) {
              handleNextClass();
            } else {
              navigate("/Setup-Fee-Expenses", {
                state: { className: classes[0].name },
                replace: true
              });
            }
          } else {
            throw new Error(result.message || "Failed to delete");
          }
        } catch (error) {
          console.error("Error deleting record:", error);
          Swal.fire({
            title: "Error!",
            text: error.message || "Failed to delete record",
            icon: "error",
            confirmButtonColor: "#d33",
          });
        }
      }
    });
  };

  const clearForm = () => {
    Swal.fire({
      title: "Clear Form?",
      text: "Are you sure you want to reset all fields?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, clear it!"
    }).then((result) => {
      if (result.isConfirmed) {
        setFormData(prev => ({
          ...prev,
          annual_charges: "",
          admission_fee: "",
          monthly_fee: "",
          book_charges: "",
          workbook_charges: "",
          total_payable: "0"
        }));
      }
    });
  };

  const handleClassChange = useCallback((direction) => {
    const newIndex = direction === "next"
      ? classIndex < classes.length - 1 ? classIndex + 1 : 0
      : classIndex > 0 ? classIndex - 1 : classes.length - 1;

    navigate("/Setup-Fee-Expenses", {
      state: { className: classes[newIndex].name },
      replace: true
    });
  }, [classIndex, navigate]);

  const handlePreviousClass = () => handleClassChange("prev");
  const handleNextClass = () => handleClassChange("next");

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      e.preventDefault();
      if (index < inputRefs.current.length - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      e.preventDefault();
      if (index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    } else if (e.key === "Enter" && e.ctrlKey) {
      e.preventDefault();
      handleSave();
    } else if (e.key === "Tab") {
      return;
    }
  };

  const formatCurrency = (amount) => {
    const num = parseFloat(amount || 0);
    return new Intl.NumberFormat('en-PK', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  const exportData = async (format = 'csv') => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/FeeExpensesController.php?action=export&format=${format}&class_name=${encodeURIComponent(formData.class_name)}`
      );

      if (response.ok) {
        if (format === 'csv') {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `fee_${formData.class_name}_${new Date().toISOString().split('T')[0]}.csv`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        } else {
          const data = await response.json();
          const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `fee_${formData.class_name}_${new Date().toISOString().split('T')[0]}.json`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        }
      }
    } catch (error) {
      console.error("Export error:", error);
      Swal.fire({
        title: "Export Failed",
        text: "Failed to export data",
        icon: "error",
        confirmButtonColor: "#d33",
      });
    }
  };

  if (loading) {
    return (
      <div className="dashboard-wrapper">
        <Sidebar />
        <div className="main-content" style={{ marginLeft: "20%" }}>
          <div className="SetupAccountsFaEx-loadingOverlay">
            <div className="SetupAccountsFaEx-spinner"></div>
            <p>Loading fee structure for {initialClassName}...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-wrapper">
      <Sidebar />
      <div className="main-content" style={{ marginLeft: "20%" }}>
        <section className="content">
          {/* Header Section */}
          <div className="SetupAccountsFaEx-header">
            <div className="SetupAccountsFaEx-headerContent">
              <h1 className="SetupAccountsFaEx-title">
                <FontAwesomeIcon icon={faCalculator} className="SetupAccountsFaEx-headerIcon" />
                {formData.class_name} - Fee & Expenses Management
                {isNewRecord && (
                  <span className="SetupAccountsFaEx-newRecordBadge">New Record</span>
                )}
              </h1>
              <p className="SetupAccountsFaEx-headerSubtitle">
                {formData.updated_at 
                  ? `Last updated: ${formData.updated_at}` 
                  : 'No data exists for this class yet'}
                {formData.id && ` | Record ID: ${formData.id}`}
              </p>
            </div>
            
            <div className="SetupAccountsFaEx-headerActions">
              <button 
                className="SetupAccountsFaEx-btn SetupAccountsFaEx-btnSecondary"
                onClick={() => setActiveTab(activeTab === 'audit' ? 'form' : 'audit')}
                title="View Audit Log"
              >
                <FontAwesomeIcon icon={faHistory} /> 
                {activeTab === 'audit' ? 'Back to Form' : 'View Audit'}
              </button>
              <div className="SetupAccountsFaEx-dropdown">
                <button className="SetupAccountsFaEx-btn SetupAccountsFaEx-btnExport">
                  <FontAwesomeIcon icon={faDownload} /> Export
                </button>
                <div className="SetupAccountsFaEx-dropdownContent">
                  <a href="#" onClick={() => exportData('csv')}>Export as CSV</a>
                  <a href="#" onClick={() => exportData('json')}>Export as JSON</a>
                </div>
              </div>
              <button 
                className="SetupAccountsFaEx-btn SetupAccountsFaEx-btnDanger"
                onClick={handleDelete}
                disabled={isNewRecord}
                title={isNewRecord ? "Cannot delete new record" : "Delete this record"}
              >
                <FontAwesomeIcon icon={faTrash} /> Delete
              </button>
            </div>
          </div>

          {/* Stats Overview */}
          <div className="SetupAccountsFaEx-statsOverview">
            <div className="SetupAccountsFaEx-statCard">
              <div className="SetupAccountsFaEx-statIcon SetupAccountsFaEx-statIconTotalClasses">
                <FontAwesomeIcon icon={faGraduationCap} />
              </div>
              <div className="SetupAccountsFaEx-statDetails">
                <div className="SetupAccountsFaEx-statValue">{stats.total_classes}</div>
                <div className="SetupAccountsFaEx-statLabel">Total Classes</div>
              </div>
            </div>
            
            <div className="SetupAccountsFaEx-statCard">
              <div className="SetupAccountsFaEx-statIcon SetupAccountsFaEx-statIconGrandTotal">
                <FontAwesomeIcon icon={faMoneyBillWave} />
              </div>
              <div className="SetupAccountsFaEx-statDetails">
                <div className="SetupAccountsFaEx-statValue">{formatCurrency(stats.total_payable)}</div>
                <div className="SetupAccountsFaEx-statLabel">Grand Total</div>
              </div>
            </div>
            
            <div className="SetupAccountsFaEx-statCard">
              <div className="SetupAccountsFaEx-statIcon SetupAccountsFaEx-statIconAverageFee">
                <FontAwesomeIcon icon={faCalculator} />
              </div>
              <div className="SetupAccountsFaEx-statDetails">
                <div className="SetupAccountsFaEx-statValue">{formatCurrency(stats.average_per_class)}</div>
                <div className="SetupAccountsFaEx-statLabel">Average per Class</div>
              </div>
            </div>
            
            <div className="SetupAccountsFaEx-statCard">
              <div className="SetupAccountsFaEx-statIcon SetupAccountsFaEx-statIconLastUpdated">
                <FontAwesomeIcon icon={faDatabase} />
              </div>
              <div className="SetupAccountsFaEx-statDetails">
                <div className="SetupAccountsFaEx-statValue">{stats.last_updated ? 'Yes' : 'No'}</div>
                <div className="SetupAccountsFaEx-statLabel">Data Updated</div>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="SetupAccountsFaEx-tabNavigation">
            <button 
              className={`SetupAccountsFaEx-tab ${activeTab === 'form' ? 'SetupAccountsFaEx-tabActive' : ''}`}
              onClick={() => setActiveTab('form')}
            >
              <FontAwesomeIcon icon={faEdit} /> Fee Structure
            </button>
            <button 
              className={`SetupAccountsFaEx-tab ${activeTab === 'audit' ? 'SetupAccountsFaEx-tabActive' : ''}`}
              onClick={() => setActiveTab('audit')}
            >
              <FontAwesomeIcon icon={faHistory} /> Audit Log
            </button>
            <button 
              className={`SetupAccountsFaEx-tab ${activeTab === 'navigation' ? 'SetupAccountsFaEx-tabActive' : ''}`}
              onClick={() => setActiveTab('navigation')}
            >
              <FontAwesomeIcon icon={faGraduationCap} /> Quick Navigation
            </button>
          </div>

          {/* Main Content Area */}
          <div className="SetupAccountsFaEx-contentArea">
            {activeTab === 'form' && (
              <form className="SetupAccountsFaEx-form" onSubmit={handleSave}>
                <div className="SetupAccountsFaEx-formSection">
                  <h3 className="SetupAccountsFaEx-sectionTitle">
                    <FontAwesomeIcon icon={faInfoCircle} /> Class Information
                  </h3>
                  
                  <div className="SetupAccountsFaEx-formRow">
                    <div className="SetupAccountsFaEx-formGroup">
                      <label className="SetupAccountsFaEx-label">
                        <FontAwesomeIcon icon={faGraduationCap} /> Class Name:
                      </label>
                      <div className="SetupAccountsFaEx-inputGroup">
                        <input
                          type="text"
                          name="class_name"
                          value={formData.class_name}
                          onChange={handleInputChange}
                          readOnly={!isNewRecord}
                          className={`SetupAccountsFaEx-input ${validationErrors.class_name ? 'SetupAccountsFaEx-inputError' : ''}`}
                        />
                        <div className="SetupAccountsFaEx-statusIndicator">
                          <span className={`SetupAccountsFaEx-statusBadge ${isNewRecord ? 'SetupAccountsFaEx-statusNew' : 'SetupAccountsFaEx-statusExisting'}`}>
                            {isNewRecord ? 'New Record' : 'Existing Record'}
                          </span>
                        </div>
                      </div>
                      {validationErrors.class_name && (
                        <span className="SetupAccountsFaEx-errorMessage">{validationErrors.class_name}</span>
                      )}
                    </div>
                  </div>

                  <h3 className="SetupAccountsFaEx-sectionTitle">
                    <FontAwesomeIcon icon={faMoneyBillWave} /> Fee Details
                  </h3>

                  <div className="SetupAccountsFaEx-formGrid">
                    <div className="SetupAccountsFaEx-formGroup">
                      <label className="SetupAccountsFaEx-label">
                        <FontAwesomeIcon icon={faCalendarAlt} /> Annual Charges:
                      </label>
                      <div className="SetupAccountsFaEx-inputWithSuffix">
                        <input
                          type="number"
                          name="annual_charges"
                          value={formData.annual_charges}
                          onChange={handleInputChange}
                          onKeyDown={(e) => handleKeyDown(e, 0)}
                          ref={(el) => (inputRefs.current[0] = el)}
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          className={`SetupAccountsFaEx-input ${validationErrors.annual_charges ? 'SetupAccountsFaEx-inputError' : ''}`}
                        />
                        <span className="SetupAccountsFaEx-inputSuffix">PKR</span>
                      </div>
                      {validationErrors.annual_charges && (
                        <span className="SetupAccountsFaEx-errorMessage">{validationErrors.annual_charges}</span>
                      )}
                    </div>

                    <div className="SetupAccountsFaEx-formGroup">
                      <label className="SetupAccountsFaEx-label">
                        <FontAwesomeIcon icon={faGraduationCap} /> Admission Fee:
                      </label>
                      <div className="SetupAccountsFaEx-inputWithSuffix">
                        <input
                          type="number"
                          name="admission_fee"
                          value={formData.admission_fee}
                          onChange={handleInputChange}
                          onKeyDown={(e) => handleKeyDown(e, 1)}
                          ref={(el) => (inputRefs.current[1] = el)}
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          className={`SetupAccountsFaEx-input ${validationErrors.admission_fee ? 'SetupAccountsFaEx-inputError' : ''}`}
                        />
                        <span className="SetupAccountsFaEx-inputSuffix">PKR</span>
                      </div>
                      {validationErrors.admission_fee && (
                        <span className="SetupAccountsFaEx-errorMessage">{validationErrors.admission_fee}</span>
                      )}
                    </div>

                    <div className="SetupAccountsFaEx-formGroup">
                      <label className="SetupAccountsFaEx-label">
                        <FontAwesomeIcon icon={faMoneyBillWave} /> Monthly Fee:
                      </label>
                      <div className="SetupAccountsFaEx-inputWithSuffix">
                        <input
                          type="number"
                          name="monthly_fee"
                          value={formData.monthly_fee}
                          onChange={handleInputChange}
                          onKeyDown={(e) => handleKeyDown(e, 2)}
                          ref={(el) => (inputRefs.current[2] = el)}
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          className={`SetupAccountsFaEx-input ${validationErrors.monthly_fee ? 'SetupAccountsFaEx-inputError' : ''}`}
                        />
                        <span className="SetupAccountsFaEx-inputSuffix">PKR</span>
                      </div>
                      {validationErrors.monthly_fee && (
                        <span className="SetupAccountsFaEx-errorMessage">{validationErrors.monthly_fee}</span>
                      )}
                    </div>

                    <div className="SetupAccountsFaEx-formGroup">
                      <label className="SetupAccountsFaEx-label">
                        <FontAwesomeIcon icon={faBook} /> Book Charges:
                      </label>
                      <div className="SetupAccountsFaEx-inputWithSuffix">
                        <input
                          type="number"
                          name="book_charges"
                          value={formData.book_charges}
                          onChange={handleInputChange}
                          onKeyDown={(e) => handleKeyDown(e, 3)}
                          ref={(el) => (inputRefs.current[3] = el)}
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          className={`SetupAccountsFaEx-input ${validationErrors.book_charges ? 'SetupAccountsFaEx-inputError' : ''}`}
                        />
                        <span className="SetupAccountsFaEx-inputSuffix">PKR</span>
                      </div>
                      {validationErrors.book_charges && (
                        <span className="SetupAccountsFaEx-errorMessage">{validationErrors.book_charges}</span>
                      )}
                    </div>

                    <div className="SetupAccountsFaEx-formGroup">
                      <label className="SetupAccountsFaEx-label">
                        <FontAwesomeIcon icon={faBook} /> Workbook Charges:
                      </label>
                      <div className="SetupAccountsFaEx-inputWithSuffix">
                        <input
                          type="number"
                          name="workbook_charges"
                          value={formData.workbook_charges}
                          onChange={handleInputChange}
                          onKeyDown={(e) => handleKeyDown(e, 4)}
                          ref={(el) => (inputRefs.current[4] = el)}
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          className={`SetupAccountsFaEx-input ${validationErrors.workbook_charges ? 'SetupAccountsFaEx-inputError' : ''}`}
                        />
                        <span className="SetupAccountsFaEx-inputSuffix">PKR</span>
                      </div>
                      {validationErrors.workbook_charges && (
                        <span className="SetupAccountsFaEx-errorMessage">{validationErrors.workbook_charges}</span>
                      )}
                    </div>

                    <div className="SetupAccountsFaEx-formGroup SetupAccountsFaEx-totalAmount">
                      <label className="SetupAccountsFaEx-label">
                        <FontAwesomeIcon icon={faCalculator} /> Total Payable:
                      </label>
                      <div className="SetupAccountsFaEx-totalDisplay">
                        <span className="SetupAccountsFaEx-totalValue">{formatCurrency(formData.total_payable)} PKR</span>
                        <small className="SetupAccountsFaEx-totalDescription">Per student per year</small>
                      </div>
                    </div>
                  </div>

                  {/* Timestamps */}
                  {(formData.created_at || formData.updated_at) && (
                    <div className="SetupAccountsFaEx-timestamps">
                      {formData.created_at && (
                        <div className="SetupAccountsFaEx-timestamp">
                          <strong>Created:</strong> {formData.created_at}
                        </div>
                      )}
                      {formData.updated_at && (
                        <div className="SetupAccountsFaEx-timestamp">
                          <strong>Last Updated:</strong> {formData.updated_at}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="SetupAccountsFaEx-actionButtons">
                  <button
                    type="button"
                    className="SetupAccountsFaEx-btn SetupAccountsFaEx-btnNavigation SetupAccountsFaEx-btnPrevClass"
                    onClick={handlePreviousClass}
                    title="Previous Class (Alt+P)"
                  >
                    <FontAwesomeIcon icon={faArrowLeft} />
                    <span>Previous Class</span>
                  </button>

                  <button
                    type="button"
                    className="SetupAccountsFaEx-btn SetupAccountsFaEx-btnClear"
                    onClick={clearForm}
                    title="Clear Form (Esc)"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                    <span>Clear Form</span>
                  </button>

                  <button
                    type="submit"
                    className="SetupAccountsFaEx-btn SetupAccountsFaEx-btnPrimary"
                    disabled={saving}
                    title="Save Changes (Ctrl+S)"
                  >
                    {saving ? (
                      <>
                        <FontAwesomeIcon icon={faSyncAlt} spin />
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faSave} />
                        <span>{isNewRecord ? 'Create Record' : 'Update Record'}</span>
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    className="SetupAccountsFaEx-btn SetupAccountsFaEx-btnNavigation SetupAccountsFaEx-btnNextClass"
                    onClick={handleNextClass}
                    title="Next Class (Alt+N)"
                  >
                    <span>Next Class</span>
                    <FontAwesomeIcon icon={faArrowRight} />
                  </button>
                </div>
              </form>
            )}

            {activeTab === 'audit' && (
              <div className="SetupAccountsFaEx-auditLog">
                <h3 className="SetupAccountsFaEx-sectionTitle">
                  <FontAwesomeIcon icon={faHistory} /> Audit Log for {formData.class_name}
                </h3>
                <div className="SetupAccountsFaEx-logContainer">
                  {auditLog.length > 0 ? (
                    auditLog.map((log, index) => (
                      <div key={index} className="SetupAccountsFaEx-logEntry">
                        <div className="SetupAccountsFaEx-logTime">{log.timestamp}</div>
                        <div className="SetupAccountsFaEx-logMessage">{log.message}</div>
                        <div className="SetupAccountsFaEx-logUser">{log.user || 'System'}</div>
                      </div>
                    ))
                  ) : (
                    <div className="SetupAccountsFaEx-noLogs">
                      No audit logs available for this class.
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'navigation' && (
              <div className="SetupAccountsFaEx-quickNavigation">
                <h3 className="SetupAccountsFaEx-sectionTitle">
                  <FontAwesomeIcon icon={faGraduationCap} /> Quick Class Navigation
                </h3>
                <div className="SetupAccountsFaEx-classGrid">
                  {classes.map((cls, idx) => (
                    <button
                      key={idx}
                      className={`SetupAccountsFaEx-classBtn ${cls.name === formData.class_name ? 'SetupAccountsFaEx-classBtnActive' : ''}`}
                      onClick={() => navigate("/Setup-Fee-Expenses", {
                        state: { className: cls.name },
                        replace: true
                      })}
                    >
                      {cls.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Keyboard Shortcuts Help */}
          <div className="SetupAccountsFaEx-shortcutsHelp">
            <details>
              <summary>
                <FontAwesomeIcon icon={faKey} /> Keyboard Shortcuts
              </summary>
              <div className="SetupAccountsFaEx-shortcutsGrid">
                <div className="SetupAccountsFaEx-shortcutItem">
                  <kbd>Alt</kbd> + <kbd>P</kbd>
                  <span>Previous Class</span>
                </div>
                <div className="SetupAccountsFaEx-shortcutItem">
                  <kbd>Alt</kbd> + <kbd>N</kbd>
                  <span>Next Class</span>
                </div>
                <div className="SetupAccountsFaEx-shortcutItem">
                  <kbd>Ctrl</kbd> + <kbd>S</kbd>
                  <span>Save Changes</span>
                </div>
                <div className="SetupAccountsFaEx-shortcutItem">
                  <kbd>Esc</kbd>
                  <span>Clear Form</span>
                </div>
                <div className="SetupAccountsFaEx-shortcutItem">
                  <kbd>Tab</kbd>
                  <span>Navigate Fields</span>
                </div>
                <div className="SetupAccountsFaEx-shortcutItem">
                  <kbd>↑/↓</kbd>
                  <span>Navigate Vertically</span>
                </div>
              </div>
            </details>
          </div>
        </section>
      </div>
    </div>
  );
};

export default SetupAccountsFaEx;